<template>
    <Head :title="request.reference + ' | Request'" />
    <div class="w-full min-h-screen flex flex-col bg-gray-100">
        <div class="bg-white flex sm:flex-row flex-col sm:items-center">
            <div class="flex flex-col flex-grow justify-center">
                <div class="max-w-7xl flex flex-grow flex-row pt-4 px-4 sm:pt-6 lg:px-8 items-center">
                    <div class="cursor-pointer" @click="back">
                        <ArrowLeftIcon class="h-7 w-7 mr-4 sm:h-8 sm:w-8 text-gray-500"/>
                    </div>
                    <div class="flex flex-col">
                        <h1 class="text-3xl font-extrabold tracking-tight text-gray-900">{{ request.reference }}</h1>
                        <span class="text-gray-500">{{ beautifiedTime(request.created_at) }}</span>
                    </div>
                </div>
                <div class="relative rounded-lg px-6 py-5 flex items-center space-x-3 sm:ml-14 lg:ml-20 ml-0">
                    <div class="flex-shrink-0">
                        <div v-if="!request.customer.photo" class="h-10 w-10 rounded-full bg-red-100"/>
                        <img v-else class="object-cover h-10 w-10 rounded-full bg-gray-200" :src="request.customer.photo" :alt="request.customer.id" />
                    </div>
                    <div class="flex-1 min-w-0">
                        <div class="focus:outline-none">
                            <div v-if="!isEditingCustomerName" @click="isEditable && toggleCustomerNameEdit()"
                                class="flex flex-row group items-center">
                                <p class="text-sm font-medium text-gray-900">{{ request.customer.name }}</p>
                                <PencilIcon v-if="isEditable" class="cursor-pointer transition-all duration-300 opacity-0 group-hover:opacity-100 h-4 w-4 ml-2 text-blue-500" />
                            </div>
                            <input ref="customerName" v-else v-model="request.customer.name"
                                    @keyup.esc="cancelNameEdit()"
                                    @blur="toggleCustomerNameEdit()"
                                    class="px-2 appearance-none w-full py-2 bg-transparent"/>
                        </div>
                    </div>
                </div>
            </div>
            <div class="flex flex-col mx-8 mb-8 sm:mb-0 sm:w-64 space-y-4">
                <tailwind-select label="Status" @change="e => statusUpdated(e)" :options="statusOptions" :selected-item="status"/>
                <div class="flex flex-row space-x-4">
                    <div class="flex-grow relative flex items-start">
                        <div class="flex items-center h-5">
                            <input id="should_mail" name="should_mail" v-model="request.should_mail" v-on:change="autoMailUpdated()" type="checkbox" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" disabled/>
                        </div>
                        <div class="ml-3 text-sm">
                            <label for="should_mail" class="font-medium text-gray-700">Auto Mail</label>
                        </div>
                    </div>
                    <div class="flex-grow relative flex items-start">
                        <div class="flex items-center h-5">
                            <input id="should_call" name="should_call" v-model="request.should_call" v-on:change="needsCallbackUpdated()" type="checkbox" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" :disabled="!isEditable"/>
                        </div>
                        <div class="ml-3 text-sm">
                            <label for="should_call" class="font-medium text-gray-700">Needs Callback</label>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <h2 id="products-heading" class="sr-only">Request {{ request.reference }}</h2>
        <div class="flex flex-col m-8">
            <h2 class="font-bold mb-8">Saved Channels</h2>
            <customer-channels
                @reload="reloadPage()"
                :disabled="!isEditable"
                :except="request.channels"
                :customer="request.customer"
                @channel-added="addChannel($event)"
                :channel-options="channelOptions"/>

            <h2 class="font-bold my-8">Channels</h2>
            <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div v-for="channel in request.channels" :key="channel.id" class="flex flex-row bg-white shadow rounded-md block hover:bg-gray-50 py-4 group relative">
                    <div v-if="isEditable" @click="removeChannel(channel)"
                        class="z-10 cursor-pointer group-hover:opacity-100 opacity-100 xl:opacity-0 absolute -top-2 -right-2 transition-opacity h-4 w-4 bg-red-500 rounded-full flex flex-col justify-center items-center">
                        <MinusIcon class="h-3 w-3 text-white"/>
                    </div>
                    <div @click.prevent.stop="copy(channel.value)"
                         class="h-12 w-12 rounded-full cursor-pointer ml-2 flex flex-col justify-center items-center"
                         :class="channel.slug !== 'phone-number' ? 'border' : 'bg-white'">
                        <ClipboardCopyIcon class="h-6 w-6 text-gray-600"/>
                    </div>
                    <div class="px-4 flex-grow">
                        <div class="flex items-center justify-between">
                            <p class="text-sm font-medium text-indigo-600 truncate">
                                {{ channel.meta.name }}
                            </p>
                            <div class="ml-2 flex-shrink-0 flex" v-if="channel.is_primary">
                                <p class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                    Primary
                                </p>
                            </div>
                        </div>
                        <div class="mt-2 flex sm:justify-between">
                            <p class="flex flex-grow items-center text-sm text-gray-500 truncate">
                                {{ channel.value }}
                            </p>
                            <div class="ml-2 flex-shrink-0 flex" v-if="false">
                                <div :class="[channel.status === 'sent' ? 'text-green-600' : 'text-gray-400']" class="px-2 inline-flex text-sm leading-5 font-semibold flex items-center">
                                    <CheckIcon v-if="channel.status === 'sent'" class="flex h-4 w-4 relative mr-2 text-green-600" />
                                    <svg v-else-if="channel.status === 'sending'" class="animate-spin -ml-1 mr-2 h-4 w-4 text-reethi-purple" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span v-else class="flex h-3 w-3 relative mr-2">
                                    <span :class="[channel.status === 'failed' ? 'bg-red-400' : 'bg-blue-400']" class="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"></span>
                                    <span :class="[channel.status === 'failed' ? 'bg-red-500' : 'bg-blue-500']" class="relative inline-flex rounded-full h-3 w-3"></span>
                                </span>
                                    {{ channel.status }}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="flex flex-row mt-2 mr-4" v-if="false && channel.meta.slug === 'phone-number'">
                        <div
                            @click.stop="toggleWhatsApp(channel)"
                            :class="channel.whatsAppSelected ? 'fill-[#25D366] border-[#25D366]' : 'fill-gray-400'"
                            class="cursor-pointer transition-all duration-300 border rounded-full p-2"><whatsapp /></div>
                        <div
                            @click.stop="toggleViber(channel)"
                            :class="channel.viberSelected ? 'fill-[#59267c] border-[#59267c]' : 'fill-gray-400'"
                            class="cursor-pointer transition-all duration-300 mx-4 border rounded-full p-2"><viber /></div>
                        <div
                            @click.stop="toggleTelegram(channel)"
                            :class="channel.telegramSelected ? 'fill-[#0088cc] border-[#0088cc]' : 'fill-gray-400'"
                            class="cursor-pointer transition-all duration-300 border rounded-full p-2"><telegram /></div>
                    </div>
                </div>
            </div>
            <div v-if="!request.channels.length" class="p-4 bg-gray-100 overflow-hidden text-gray-400 flex justify-center items-center">
                No Channels Set
            </div>
            <h2 class="font-bold mt-8 mb-4">Notes</h2>
            <div class="flex-1 min-w-0">
                <div class="focus:outline-none">
                    <div v-if="!isEditingNotes" @click="isEditable && toggleNotesEdit()"
                         class="flex flex-row group items-center">
                        <pre class="text-sm font-medium text-gray-900">{{ request.notes ?? "No Notes" }}</pre>
                        <PencilIcon v-if="isEditable" class="cursor-pointer transition-all duration-300 opacity-0 group-hover:opacity-100 h-4 w-4 ml-2 text-blue-500" />
                    </div>
                    <textarea ref="notes" v-else v-model="request.notes"
                           @keyup.esc="cancelNotesEdit()"
                           @blur.prevent="toggleNotesEdit()"
                           class="w-full py-2 bg-transparent appearance-none"/>
                </div>
            </div>
            <h2 class="font-bold my-8">Photos ({{ request.images?.length ?? '0' }})</h2>
            <div class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3">
                <image-card
                    :disabled="!isEditable"
                    v-for="image in request.images"
                    :image="image"
                    :categories="categoryOptions"
                    @updated="updateImage(image)"
                    @uploaded="(pay) => imageUploaded(pay)"
                    @delete="removeImage(image)"
                    @duplicate="duplicateImage(image)"
                />
                <div v-if="isEditable"
                    @click="newImage()"
                    class="p-8 cursor-pointer hover:bg-gray-200 bg-gray-100 overflow-hidden rounded-md border border-dashed border-gray-300 text-gray-400 flex flex-row justify-center items-center">
                    <PlusIcon class="w-4 h-4 mr-4"/>
                    Add Photo
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from "axios";
import { isProxy, toRaw } from "vue";
import Layout from "@/Shared/Layout";
import { Head } from "@inertiajs/inertia-vue3";
import Viber from "@/Pages/Orders/Icons/viber";
import Telegram from "@/Pages/Orders/Icons/telegram";
import Whatsapp from "@/Pages/Orders/Icons/whatsapp";
import ImageCard from "@/Pages/Orders/Components/ImageCard";
import TailwindSelect from "@/Shared/Components/TailwindSelect";
import { beautifiedTime, copyToClipboard } from "@/Utils/functions";
import CustomerChannels from "@/Pages/Customers/components/CustomerChannels";
import {
    PlusIcon,
    MinusIcon,
    CheckIcon,
    PencilIcon,
    ArrowLeftIcon,
    ClipboardCopyIcon
} from "@heroicons/vue/solid";

export default {
    layout: Layout,
    props: {
        data: Object,
        statusList: Array,
        categories: Array,
        availableChannels: Array,
    },
    components: {
        Head,
        ImageCard,
        TailwindSelect,
        CustomerChannels,

        Viber,
        Whatsapp,
        Telegram,
        PlusIcon,
        CheckIcon,
        MinusIcon,
        PencilIcon,
        ArrowLeftIcon,
        ClipboardCopyIcon,
    },
    computed: {
        isEditable() {
            return this.request.status === 'new'
                || this.request.status === 'on_hold'
                || this.request.status === 'processing'
        },
        customerPrimaryChannels() {
            return this.request.customer.channels.filter(ch => ch.is_primary)
        },
        savedAvailableChannels() {
            return (this.request.customer.channels || [])
                .filter(ch => (this.request.channels || [])
                    .filter(dch => ch.slug === dch.meta.slug && ch.value === dch.value).length === 0);
        }
    },
    data() {
        return {
            notesCache: "",
            shiftKey: false,
            customerNameCache: "",
            isEditingNotes: false,
            isEditingCustomerName: false,
            request: JSON.parse(JSON.stringify(isProxy(this.data) ? toRaw(this.data) : this.data)),
        };
    },
    setup(props) {
        return {
            status: props.statusList.filter(s => s.value === props.data.status)[0],
            statusOptions: isProxy(props.statusList) ? toRaw(props.statusList) : props.statusList,
            categoryOptions: isProxy(props.categories) ? toRaw(props.categories) : props.categories,
            channelOptions: isProxy(props.availableChannels) ? toRaw(props.availableChannels) : props.availableChannels,
        };
    },
    updated() {
        if (this.request.customer.channels.length !== this.data.customer.channels.length) {
            this.request.customer.channels = JSON.parse(JSON.stringify(
                isProxy(this.data.customer.channels)
                ? toRaw(this.data.customer.channels)
                : this.data.customer.channels
            ));
        }
        if (this.statusOptions.filter(s => s.value === "ready")[0].disabled !== this.statusList.filter(s => s.value === "ready")[0].disabled) {
            this.statusOptions = isProxy(this.statusList) ? toRaw(this.statusList) : this.statusList
            this.$forceUpdate();
        }
    },
    mounted() {
        this.$echo.private('request_updated.' + this.data.reference).listen('.updated', message => {
            this.$inertia.reload({
                preserveScroll: true,
            })
        });
        document.addEventListener('keydown', this.pageKeyDown);
        document.addEventListener('keyup', this.pageKeyUp);
    },
    beforeUnmount() {
        this.$echo.leave('request_updated.' + this.data.reference)
        document.removeEventListener('keydown', this.pageKeyDown);
        document.removeEventListener('keyup', this.pageKeyUp);
    },
    methods: {
        beautifiedTime,
        back() {
            window.history.back();
        },
        async copy(text) {
            try {
                await copyToClipboard(text)
                this.$page.props.flash = {
                    success: "Copied to clipboard"
                }
            } catch (e) {
                this.$page.props.flash = {
                    error: "Failed to copy to clipboard"
                }
            }
        },
        async toggleNotesEdit() {
            this.notesCache = this.request.notes
            this.isEditingNotes = !this.isEditingNotes;
            if (!this.isEditingNotes) {
                await axios.patch(
                    this.$route('orders.update', {
                        id: this.request.reference,
                    }), {notes: this.request.notes.trim()}
                )
            } else {
                await this.$nextTick(() => this.$refs.notes.focus())
            }
        },
        cancelNotesEdit() {
            this.isEditingNotes = !this.isEditingNotes;
            this.request.notes = this.notesCache;
        },
        async toggleCustomerNameEdit() {
            this.customerNameCache = this.request.customer.name
            this.isEditingCustomerName = !this.isEditingCustomerName;
            if (!this.isEditingCustomerName) {
                await axios.patch(
                    this.$route('customers.update', {
                        id: this.request.customer.id,
                    }), {name: this.request.customer.name}
                )
            } else {
                await this.$nextTick(() => this.$refs.customerName.focus())
            }
        },
        cancelNameEdit() {
            this.isEditingCustomerName = !this.isEditingCustomerName;
            this.request.customer.name = this.customerNameCache;
        },
        async autoMailUpdated() {
            await axios.patch(
                this.$route('orders.update', {
                    reference: this.request.reference,
                }), {should_mail: this.request.should_mail}
            )
        },
        async needsCallbackUpdated() {
            await axios.patch(
                this.$route('orders.update', {
                    reference: this.request.reference,
                }), {should_call: this.request.should_call}
            )
        },
        async statusUpdated($event) {
            this.status = $event;
            if ($event.value === "ready") {
                this.$echo.leave('request_updated.' + this.data.reference)
                axios.patch(
                    this.$route('orders.update', {
                        reference: this.request.reference,
                    }), {status: $event.value}
                ).then(_ => {
                    this.$inertia.reload({
                        preserveScroll: true
                    })
                });
                this.$inertia.visit(this.$route('orders.index'))
            } else {
                await axios.patch(
                    this.$route('orders.update', {
                        reference: this.request.reference,
                    }), {status: $event.value}
                )
            }
        },
        duplicateImage(image) {
            let clone = JSON.parse(JSON.stringify(image))
            delete clone.status;
            delete clone.category;
            delete clone.reference;
            delete clone.file_name;
            delete clone.uploaded_image_file_name;

            if (!clone.custom_size || clone.custom_size === "") {
                delete clone.custom_size;
            }

            if (!clone.notes || clone.notes === "") {
                delete clone.notes;
            }

            if (!clone.cc_email || clone.cc_email === "") {
                delete clone.cc_email;
            }

            this.newImage(clone)
        },
        async addChannel(channel) {
            this.request.channels.push({
                id: channel.pivot_id,
                is_primary: channel.is_primary,
                meta: {
                    id: channel.id,
                    name: channel.name,
                    slug: channel.slug,
                    active: channel.active,
                    created_at: channel.created_at,
                    updated_at: channel.updated_at
                },
                status: 'waiting',
                value: channel.value
            });

            await axios.patch(
                this.$route('orders.micro.channels.update', {
                    reference: this.request.reference,
                    channel: channel.pivot_id
                })
            )

            this.$inertia.reload({
                preserveState: true,
                preserveScroll: true,
            })
        },
        deleteChannel(channel) {
            this.request.channels = this.request.channels.filter(ch => channel.id !== ch.id);
        },
        async removeChannel(channel) {
            this.request.channels = this.request.channels.filter(ch => channel.id !== ch.id);

            await axios.delete(
                this.$route('orders.micro.channels.delete', {
                    reference: this.request.reference,
                    channel: channel.id
                })
            )
        },
        async updateImage(image) {
            if (image.reference) {
                let clone = JSON.parse(JSON.stringify(image))
                delete clone.status;
                if (!clone.notes || clone.notes === "") {
                    delete clone.notes;
                }
                if (!clone.file_name || clone.file_name === "") {
                    delete clone.file_name;
                }
                if (!clone.custom_size || clone.custom_size === "") {
                    delete clone.custom_size;
                }
                if (!clone.cc_email || clone.cc_email === "") {
                    delete clone.cc_email;
                }

                try {
                    await axios.patch(
                        this.$route('orders.micro.images.update', {
                            reference: this.request.reference,
                            imageRef: clone.reference,
                        }), clone
                    )

                    this.reloadPage();
                } catch (e) {
                    this.$page.props.flash = {
                        error: Object.values(e.response.data.errors)[0][0].replace(".0", "")
                    }
                }
            }
        },
        async imageUploaded({image, file, done}) {
            let formData = new FormData();
            formData.append("file", file);
            try {
                await axios.post(
                    this.$route('orders.micro.images.upload', {
                        imageRef: image.reference,
                        reference: this.request.reference,
                    }), formData, {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    }
                )
                done();
            } catch (e) {
                console.log(e);
                this.$page.props.flash = {
                    error: Object.values(e.response.data.errors)[0][0].replace(".0", "")
                }
            }
        },
        async removeImage(image) {
            if (image.reference) {
                await axios.delete(
                    this.$route('orders.micro.images.delete', {
                        reference: this.request.reference,
                        imageRef: image.reference,
                    })
                )

                this.reloadPage();
            }

            this.request.images = this.request.images.filter(img => img !== image);
        },
        async newImage(payload) {
            try {
                let image = await axios.post(
                    this.$route('orders.micro.images.store', {
                        reference: this.request.reference,
                    }), payload
                )
                this.request.images.push(image.data.data);
            } catch (e) {
                if (e.response) {
                    this.$page.props.flash = {
                        error: Object.values(e.response.data.errors)[0][0].replace(".0", "")
                    }
                } else {
                    console.error(e)
                }
            }
        },
        toggleWhatsApp(channel) {
            channel.whatsAppSelected = !channel.whatsAppSelected;
        },
        toggleViber(channel) {
            channel.viberSelected = !channel.viberSelected;
        },
        toggleTelegram(channel) {
            channel.telegramSelected = !channel.telegramSelected;
        },
        pageKeyDown(event) {
            if (event.key === 'Shift') {
                this.shiftKey = true;
            } else if (this.shiftKey && event.key === 'Enter' && !this.isEditingNotes) {
                if (!this.statusOptions.filter(s => s.value === "ready")[0].disabled) {
                    this.statusUpdated(this.statusOptions.filter(s => s.value === "ready")[0])
                } else {
                    this.$page.props.flash = {
                        error: "Please check if you have a mailable channel selected, and images are uploaded"
                    }
                }
            }
        },
        pageKeyUp(event) {
            if (event.key === 'Shift') {
                this.shiftKey = false;
            }
        },
        reloadPage() {
            this.$inertia.reload({
                preserveScroll: true,
            })
        }
    }
}
</script>
